import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

import { Button } from "../../../../components/button/Button";
import { buildPhaseModel, canStepBack } from "./phaseModel";

// Winner chips for a comparison phase (§10.3) - finally shows the Q1/Q2 answers back to the
// player (critique 3.1). Winners come from the answered `comparisons` history in the payload.
const ComparisonWinners = ({ t, comparisons }) => {
  if (!comparisons.length) {
    return <p className="topik-journey__phase-note">{t("topik-progress.journey.no-picks-yet")}</p>;
  }
  return (
    <ul className="topik-journey__picks">
      {comparisons.map((comparison) => (
        <li key={`${comparison.polarity}-${comparison.slot}`} className="topik-journey__pick">
          <span className="topik-journey__pick-slot">
            {t(`topik-progress.slots.${comparison.slot}`)}
          </span>
          <span className="topik-journey__pick-body">
            <span className="topik-journey__pick-card">
              {comparison.outcomeCard ? comparison.outcomeCard.title : "—"}
            </span>
            {/* D1 echo (§10.3): the card the player set aside for this pick. */}
            {comparison.rejectedCard && (
              <span className="topik-journey__pick-rejected">
                {t("topik-progress.journey.set-aside", { card: comparison.rejectedCard.title })}
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
};

ComparisonWinners.propTypes = {
  t: PropTypes.func.isRequired,
  comparisons: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const phaseIconClass = (polarity) => (
  `topik-journey__phase-icon topik-journey__phase-icon--${polarity || "neutral"}`
);

const stateLabelKey = {
  done: "topik-progress.journey.state-done",
  current: "topik-progress.journey.state-current",
  upcoming: "topik-progress.journey.state-upcoming",
};

// On-demand full-height sheet showing what has happened (winners) and what is to come
// (per-phase descriptions incl. the short-circuit branch), plus the analysis back control.
const TopikJourneyMap = ({ t, open, session, onClose, onBack }) => {
  const { steps = [], step, comparisons = [], shortCircuited } = session;
  const groups = buildPhaseModel(steps, step);
  const showBack = canStepBack(step);
  const sheetRef = useRef(null);

  const winnersFor = (polarity) => comparisons.filter((c) => c.polarity === polarity);

  // A11y (§05 4d / Stream F): Escape closes the sheet and Tab is trapped inside it while open,
  // and focus moves into the sheet on open so keyboard users are not stranded behind it.
  useEffect(() => {
    if (!open) return undefined;

    const trapTab = (e) => {
      if (!sheetRef.current) return;
      const nodes = Array.from(sheetRef.current.querySelectorAll(FOCUSABLE))
        .filter((n) => !n.disabled && n.offsetParent !== null);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "Tab") {
        trapTab(e);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    const focusTimer = setTimeout(() => {
      if (sheetRef.current) {
        const focusable = sheetRef.current.querySelector(FOCUSABLE);
        (focusable || sheetRef.current).focus();
      }
    }, 0);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      clearTimeout(focusTimer);
    };
  }, [open, onClose]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <Helmet>
            <body className="blur-application-foreground scroll-lock" />
          </Helmet>
          <motion.div
            className="topik-journey"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <motion.div
              className="topik-journey__sheet"
              ref={sheetRef}
              role="dialog"
              aria-modal="true"
              aria-label={t("topik-progress.journey.title")}
              tabIndex={-1}
              initial={{ y: 40 }}
              animate={{ y: 0 }}
              exit={{ y: 40 }}
            >
              <div className="topik-journey__header">
                <h2 className="topik-journey__title">{t("topik-progress.journey.title")}</h2>
                <button
                  type="button"
                  className="topik-journey__close"
                  onClick={onClose}
                  aria-label={t("topik-progress.journey.close")}
                >
                  <CloseIcon />
                </button>
              </div>

              <div className="topik-journey__phases">
                {groups.map((group) => {
                  const isComparison = group.key === "harmful" || group.key === "helpful";
                  return (
                    <section
                      key={group.key}
                      className={`topik-journey__phase topik-journey__phase--${group.state}`}
                    >
                      <header className="topik-journey__phase-head">
                        <span
                          className={phaseIconClass(group.meta.polarity)}
                          aria-hidden="true"
                        >
                          {group.meta.icon}
                        </span>
                        <span className="topik-journey__phase-name">
                          {t(`topik-progress.phases.${group.key}`)}
                        </span>
                        <span className="topik-journey__phase-state">
                          {group.state === "done" && (
                            <CheckCircleIcon className="topik-journey__phase-done-icon" />
                          )}
                          {t(stateLabelKey[group.state])}
                        </span>
                      </header>

                      {isComparison && (
                        <ComparisonWinners
                          t={t}
                          comparisons={winnersFor(group.meta.polarity)}
                        />
                      )}

                      {group.key === "observation" && (
                        <p className="topik-journey__phase-note">
                          {t("topik-progress.journey.observation-desc")}
                        </p>
                      )}

                      {group.key === "analysis" && (
                        <p className="topik-journey__phase-note">
                          {shortCircuited
                            ? t("topik-progress.journey.concluded-early")
                            : t("topik-progress.journey.analysis-desc")}
                        </p>
                      )}
                    </section>
                  );
                })}
              </div>

              {showBack && (
                <div className="topik-journey__actions">
                  <Button
                    style="subtle"
                    content={t("topik-progress.journey.back-a-step")}
                    onClick={() => {
                      onBack();
                      onClose();
                    }}
                  />
                </div>
              )}
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.getElementById("overlay-container"),
  );
};

TopikJourneyMap.propTypes = {
  t: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  session: PropTypes.shape({
    steps: PropTypes.arrayOf(PropTypes.string),
    step: PropTypes.string,
    comparisons: PropTypes.arrayOf(PropTypes.object),
    shortCircuited: PropTypes.bool,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default withTranslation("views")(TopikJourneyMap);
